const PARENT_TOKEN = 'cg.parentToken';
const DEVICE_TOKEN = 'cg.deviceToken';
const STUDENT_TOKEN = 'cg.studentToken';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the session lasts until the page closes */
  }
}

export const tokens = {
  parent: () => read(PARENT_TOKEN),
  setParent: (t: string | null) => write(PARENT_TOKEN, t),
  device: () => read(DEVICE_TOKEN),
  setDevice: (t: string | null) => write(DEVICE_TOKEN, t),
  student: () => read(STUDENT_TOKEN),
  setStudent: (t: string | null) => write(STUDENT_TOKEN, t),
};

const ADMIN_TOKEN = 'cg.adminToken';
const IMPERSONATING = 'cg.impersonating';

/**
 * An admin viewing the dashboard as another parent. The admin's own session is set aside while
 * the parent's session is active, and restored when they return to the admin portal.
 */
export const impersonation = {
  /** The parent's name while impersonating, otherwise null. */
  active: () => read(IMPERSONATING),
  start(token: string, name: string) {
    const own = read(PARENT_TOKEN);
    if (!read(ADMIN_TOKEN) && own) write(ADMIN_TOKEN, own);
    write(PARENT_TOKEN, token);
    write(IMPERSONATING, name);
  },
  /** Sign out of the parent's session and go back to the admin's own. */
  async end() {
    await request(read(PARENT_TOKEN), 'POST', '/api/auth/logout', {}).catch(() => undefined);
    const own = read(ADMIN_TOKEN);
    write(IMPERSONATING, null);
    write(ADMIN_TOKEN, null);
    write(PARENT_TOKEN, own);
  },
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    /** The whole answer, for the extra fields some errors carry (like a taken code's suggestions, #898). */
    public body?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** Set while the billing page (subscribe / choose a plan) is showing, so a locked answer doesn't reload it again (#638). */
let billingPageShown = false;
export const setBillingPageShown = (on: boolean) => {
  billingPageShown = on;
};

async function request<T>(token: string | null, method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError('Can’t reach ClassGuardian. Check the internet connection.', 0);
  }
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : undefined;
  // The family became locked for billing (#638), for example a new family that still has to choose its plan: start
  // over, so the app opens on the plans page. Only for parents; the plans page itself only uses routes that stay open.
  if (res.status === 402 && data?.code === 'billing-locked' && token && token === tokens.parent() && !billingPageShown) {
    window.location.assign('/');
    return new Promise<T>(() => undefined);
  }
  if (!res.ok) throw new ApiError(data?.error ?? `Something went wrong (${res.status}).`, res.status, data ?? undefined);
  return data as T;
}

/** Calls made by the parent dashboard. */
export const api = {
  get: <T>(path: string) => request<T>(tokens.parent(), 'GET', path),
  post: <T>(path: string, body?: unknown) => request<T>(tokens.parent(), 'POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>(tokens.parent(), 'PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>(tokens.parent(), 'PATCH', path, body),
  del: <T>(path: string) => request<T>(tokens.parent(), 'DELETE', path),
};

/** Download a native agent installer (authenticated) — Windows .exe or Mac .pkg (#255) — through the browser. */
export async function downloadAgent(version: string, platform: 'windows' | 'mac' = 'windows'): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/agent/download${platform === 'mac' ? '?platform=mac' : ''}`, { headers: { Authorization: `Bearer ${tokens.parent()}` } });
  } catch {
    throw new ApiError('Can’t reach ClassGuardian. Check the internet connection.', 0);
  }
  if (!res.ok) {
    const data = res.headers.get('content-type')?.includes('json') ? await res.json() : undefined;
    throw new ApiError(data?.error ?? `Couldn’t download the app (${res.status}).`, res.status);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = platform === 'mac' ? `ClassGuardianMacSetup-${version}.pkg` : `ClassGuardianAgentSetup-${version}.exe`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Download the extension's package for the stores (#640): admins only, saved through the browser. */
export async function downloadExtension(version: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch('/api/admin/extension/download', { headers: { Authorization: `Bearer ${tokens.parent()}` } });
  } catch {
    throw new ApiError('Can’t reach ClassGuardian. Check the internet connection.', 0);
  }
  if (!res.ok) {
    const data = res.headers.get('content-type')?.includes('json') ? await res.json() : undefined;
    throw new ApiError(data?.error ?? `Couldn’t download the extension (${res.status}).`, res.status);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `ClassGuardianExtension-${version}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Fetch a file the API serves (a request's attachment, #446) with the parent's or student's sign-in. */
export async function fetchFile(path: string, who: 'parent' | 'student' = 'parent'): Promise<Blob> {
  const token = who === 'student' ? tokens.student() : tokens.parent();
  const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).catch(() => {
    throw new ApiError('Can’t reach ClassGuardian. Check the internet connection.', 0);
  });
  if (!res.ok) throw new ApiError(`Couldn’t open that file (${res.status}).`, res.status);
  return res.blob();
}

/** Calls made by the child's device. */
export const deviceApi = {
  get: <T>(path: string) => request<T>(tokens.device(), 'GET', path),
  post: <T>(path: string, body?: unknown) => request<T>(tokens.device(), 'POST', path, body ?? {}),
};

/** Calls made by a student signed into the Student Portal (#136). */
export const studentApi = {
  get: <T>(path: string) => request<T>(tokens.student(), 'GET', path),
  post: <T>(path: string, body?: unknown) => request<T>(tokens.student(), 'POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>(tokens.student(), 'PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>(tokens.student(), 'PATCH', path, body),
  del: <T>(path: string) => request<T>(tokens.student(), 'DELETE', path),
};
